//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { theme } from '@react/components/theme'
import { erc20WeiToCommaDecString } from '@shared/conversions'

import { AddressPill } from '@react/components/addresses/AddressPill'
import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtSelector } from '@react/redux/store'
import { acceptEarlyRedeemRequest } from '@react/signedTxs/acceptEarlyRedeemRequestTx'

interface EarlyReddeemRequestData {
  amount: number
  principal: number
  symbol: string
}

export const EarlyWithdrawInfoRow = ({ lenderAddr, data }: { lenderAddr: EthAddr; data: EarlyReddeemRequestData }) => (
  <Box>
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <TableHeaderItem>Lender</TableHeaderItem>
      </Grid>
      <Grid item xs={4}>
        <TableHeaderItem>Request Amount</TableHeaderItem>
      </Grid>
      <Grid item xs={4}>
        <TableHeaderItem>Principal({data.symbol})</TableHeaderItem>
      </Grid>
    </Grid>

    <Grid container sx={{ marginBottom: 4 }}>
      <Grid item xs={4}>
        <Box sx={{ width: '75%' }}>
          <AddressPill addr={lenderAddr} />
        </Box>
      </Grid>
      <Grid item xs={4}>
        <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
          {erc20WeiToCommaDecString(data.amount)} {data.symbol}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
          {erc20WeiToCommaDecString(data.principal)} {data.symbol}
        </Typography>
      </Grid>
    </Grid>
  </Box>
)

export default function BorrowerAcceptEarlyRedeemRequestForm(props: {
  poolAddr: EthAddr
  lenderAddr: EthAddr
  data?: EarlyReddeemRequestData
}): ReactElement {
  const { poolAddr, lenderAddr, data } = props
  const chainState = useOtSelector((state) => state.chainState)

  const [submitting, setSubmitting] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
  }

  const acceptEarlyRedeemRequestClick = async (): Promise<void> => {
    setSubmitting(true)
    try {
      const { webSocket, userConnection } = chainState
      const { chainConfigurationName } = chainState.chainConfiguration

      if (!userConnection?.signer) {
        throw new Error('Signer is not available')
      }

      await acceptEarlyRedeemRequest(webSocket, userConnection.signer, poolAddr, lenderAddr, chainConfigurationName)

      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (error) {
      console.error('Accept early redeem request failed:', error)
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <div data-testid="ACCEPT_EARLY_REDEEM_FORM">
      <Typography variant="body" sx={{ marginTop: 10, marginBottom: 2 }}>
        Please accept the Early Withdrawal Request. Once accepted, loan collateral should be liquidated and the Early
        Withdrawal Amount set.
      </Typography>
      <Box>{data !== undefined && <EarlyWithdrawInfoRow lenderAddr={lenderAddr} data={data} />}</Box>
      <Button
        disabled={submitting}
        sx={sx}
        onClick={() => {
          acceptEarlyRedeemRequestClick()
        }}
        variant={'contained'}
      >
        <Typography variant={'buttonBold'}>Accept Early Withdraw Request</Typography>
      </Button>
    </div>
  )
}
